import React from 'react';
import * as Yup from 'yup';
import { Form, FormikProps, withFormik } from 'formik';

// Components
import { Button } from '@capintel/component-lib/button';
import { SecondaryLink } from '../../../../components/buttons';
import { FormInput } from '../../../../components/forms';

// Utils
import { FormattedMessage, formatMessage } from '../../../../../utils/i18n';

type FormProps = {
  code: string;
};

type Props = FormikProps<FormProps> & {
  gracePeriod?: number;
  helpUrl: string;
  loading: boolean;
  primaryAction: (code: string) => Promise<void>;
  goToStep: () => void;
  onCancel?: () => void;
};

const canSubmit = (props: Props) => {
  const { values, initialValues, isSubmitting, errors } = props;

  const hasErrors = Object.keys(errors).length > 0;
  const isDirty = values !== initialValues;

  return !hasErrors && isDirty && !isSubmitting;
};

const ConfirmationStep = (props: Props) => {
  const { gracePeriod, helpUrl, onCancel, loading, goToStep } = props;

  return (
    <Form>
      <div className="two-factor-step-content two-factor-confirmation-step">
        <h3 className="two-factor-auth-header">
          <FormattedMessage id="TwoFactorAuthentication.Headers.ConfirmationCode" />
        </h3>
        <p className="two-factor-auth-text">
          <FormattedMessage id="TwoFactorAuthentication.Texts.FollowTheInstructions" />
          &nbsp;
          <a rel="noreferrer" target="_blank" href={helpUrl}>
            <FormattedMessage id="TwoFactorAuthentication.Buttons.FindOutMore" />
          </a>
        </p>

        <FormInput
          name="code"
          type="text"
          label={formatMessage('TwoFactorAuthentication.Headers.ConfirmationCode')}
          autoComplete="off"
          className="two-factor-auth-input mt-4"
          placeholder={formatMessage('TwoFactorAuthentication.Headers.EnterYourConfirmationCode')}
        />
        <div className="text-center mt-4">
          <SecondaryLink className="font-lg" onClick={goToStep}>
            <FormattedMessage id="TwoFactorAuthentication.Buttons.ScanAgain" />
          </SecondaryLink>
        </div>
        <div className="two-factor-actions">
          <Button variant="primary" loading={loading} type="submit" disabled={!canSubmit(props)}>
            <FormattedMessage id="TwoFactorAuthentication.Buttons.Next" />
          </Button>
          {onCancel &&
            (!gracePeriod ? (
              <SecondaryLink className="two-factor-cancel" onClick={onCancel}>
                <FormattedMessage id="TwoFactorAuthentication.Buttons.Cancel" />
              </SecondaryLink>
            ) : (
              <p>
                <FormattedMessage id="TwoFactorAuthentication.Texts.GracePeriod" values={{ count: gracePeriod }} />
                <SecondaryLink onClick={onCancel}>
                  <FormattedMessage id="TwoFactorAuthentication.Buttons.SkipForNow" />
                </SecondaryLink>
              </p>
            ))}
        </div>
      </div>
    </Form>
  );
};

const TwoFactorAuth = Yup.object().shape({
  code: Yup.string().required(),
});

const formikWrapper = withFormik<Props, FormProps>({
  validationSchema: TwoFactorAuth,
  mapPropsToValues: () => ({ code: '' }),
  handleSubmit: ({ code }, { props, setSubmitting }) => {
    props.primaryAction(code).finally(() => setSubmitting(false));
  },
});

export default formikWrapper(ConfirmationStep) as any;
